import React, { useState } from "react";
import { View, TextInput as RNTextInput, StyleSheet, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Skeleton } from "@/components/ui/Skeleton";

interface TextInputProps {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  error?: string;
  loading?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
}

export function TextInput({
  label,
  value,
  onChangeText,
  placeholder = "",
  required = false,
  editable = true,
  error,
  loading = false,
  multiline = false,
  numberOfLines = 1,
}: TextInputProps) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const hasError = !!error;

  // Loading state - show skeleton
  if (loading) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <Skeleton width="100%" height={multiline ? 44 * numberOfLines : 44} radius={8} />
      </View>
    );
  }

  // Readonly state
  if (!editable) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View
          style={[
            styles.inputContainer,
            multiline && { height: "auto", minHeight: 44, paddingVertical: 10 },
            { borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
          ]}
        >
          <ThemedText style={[styles.readonlyText, { color: value ? colors.text : colors.placeholder }]}>
            {value || "—"}
          </ThemedText>
        </View>
      </View>
    );
  }

  const borderColor = hasError ? colors.ruby?.border || "red" : isFocused ? colors.primary : colors.border;

  return (
    <View style={styles.container}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && (
            <ThemedText style={styles.label}>
              {label}
              {required && <ThemedText style={{ color: colors.ruby?.text || "red" }}> *</ThemedText>}
            </ThemedText>
          )}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              {error}
            </ThemedText>
          )}
        </View>
      )}
      <View
        style={[
          styles.inputContainer,
          multiline && { height: "auto", minHeight: 44, paddingVertical: 10 },
          { borderColor, backgroundColor: colors.backgroundSecondary },
        ]}
      >
        <RNTextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholder}
          style={[styles.input, multiline && { textAlignVertical: "top" }, { color: colors.text }]}
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            const trimmed = value.trim();
            if (trimmed !== value) {
              onChangeText(trimmed);
            }
          }}
          multiline={multiline}
          numberOfLines={numberOfLines}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
  },
  input: {
    flex: 1,
    fontSize: 15,
    height: "100%",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  readonlyText: {
    fontSize: 15,
    flex: 1,
  },
  errorText: {
    fontSize: 12,
  },
});
